/**
 * UatTelemetryCard — Feb 2026 admin dashboard tile.
 *
 * Three read-only aggregates sourced from /api/admin/telemetry/*:
 *   1. Bot Shield (reCAPTCHA verify verdicts + avg score by endpoint)
 *   2. Onboarding funnel (email/password vs Google OAuth)
 *   3. Free-tier gate hits (which institutional features get bumped into most)
 *
 * All queries are windowed (default 24h for reCAPTCHA, 7 days for the rest).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Lock, RefreshCw, Activity } from "lucide-react";

export default function UatTelemetryCard() {
    const [rc,      setRc]      = useState(null);
    const [onb,     setOnb]     = useState(null);
    const [gates,   setGates]   = useState(null);
    const [behav,   setBehav]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [a, b, c, d] = await Promise.all([
                api.get("/admin/telemetry/recaptcha?hours=24"),
                api.get("/admin/telemetry/onboarding?hours=168"),
                api.get("/admin/telemetry/feature-limits?hours=168"),
                api.get("/admin/telemetry/behavioral"),
            ]);
            setRc(a.data); setOnb(b.data); setGates(c.data); setBehav(d.data);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error("Failed to load telemetry.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="uat-telemetry-card">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold tracking-tight">UAT telemetry</h2>
                <button
                    onClick={load}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded border border-border hover:bg-muted/50 disabled:opacity-50"
                    data-testid="uat-telemetry-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            {error && (
                <div className="p-3 rounded border border-destructive/40 bg-destructive/5 text-xs text-destructive" data-testid="uat-telemetry-error">
                    {error}
                </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                <BotShieldTile rc={rc} loading={loading} />
                <OnboardingTile onb={onb} loading={loading} />
                <FeatureLimitsTile gates={gates} loading={loading} />
                <BehavioralTelemetryTile behav={behav} loading={loading} />
            </div>
        </div>
    );
}

/* ─── reCAPTCHA / Bot Shield ─────────────────────────────────────────────── */
function BotShieldTile({ rc, loading }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="tile-botshield">
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Bot Shield · 24h</div>
                    <div className="text-xs text-muted-foreground">Google reCAPTCHA v3</div>
                </div>
            </div>
            {loading || !rc ? (
                <div className="text-xs text-muted-foreground">Loading…</div>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-1.5 mb-3">
                        <Stat label="Verified" value={rc.total - rc.rejected - (rc.skipped || 0)} testId="botshield-verified" />
                        <Stat label="Rejected" value={rc.rejected} tone="bad" testId="botshield-rejected" />
                        <Stat label="% Bad"    value={`${rc.rejection_pct}%`} testId="botshield-pct" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Avg score by action</div>
                    <div className="space-y-1">
                        {(rc.by_action || []).map((a) => (
                            <div key={a.action} className="flex items-center justify-between gap-2 text-xs" data-testid={`botshield-action-${a.action}`}>
                                <span className="font-mono truncate">{a.action}</span>
                                <span className="proportional-nums">
                                    <span className="font-bold">{a.avg_score}</span>
                                    <span className="text-muted-foreground ml-2">({a.verifications})</span>
                                </span>
                            </div>
                        ))}
                        {(rc.by_action || []).length === 0 && (
                            <div className="text-xs text-muted-foreground">No traffic in window.</div>
                        )}
                    </div>
                    {(rc.top_errors || []).length > 0 && (
                        <div className="mt-3 pt-2 border-t border-border">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Top error codes</div>
                            <div className="flex flex-wrap gap-1">
                                {rc.top_errors.map((e) => (
                                    <span key={e.code} className="text-[10px] px-1.5 py-0.5 rounded bg-muted font-mono" data-testid={`botshield-err-${e.code}`}>
                                        {e.code} · {e.count}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {rc.skipped > 0 && (
                        <div className="mt-2 text-[10px] text-muted-foreground italic">{rc.skipped} verify(s) fail-open (key/network).</div>
                    )}
                </>
            )}
        </div>
    );
}

/* ─── Onboarding funnel ──────────────────────────────────────────────────── */
function OnboardingTile({ onb, loading }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="tile-onboarding">
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <UserPlus className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Onboarding · 7d</div>
                    <div className="text-xs text-muted-foreground">Signup path funnel</div>
                </div>
            </div>
            {loading || !onb ? (
                <div className="text-xs text-muted-foreground">Loading…</div>
            ) : (
                <>
                    <div className="text-3xl font-mono font-extrabold mb-2" data-testid="onboarding-total">{onb.total}</div>
                    <div className="space-y-1">
                        {(onb.by_path || []).map((p) => (
                            <div key={p.path} className="flex items-center justify-between gap-2 text-xs" data-testid={`onboarding-path-${p.path}`}>
                                <span className="font-mono truncate">{p.path}</span>
                                <span className="font-bold proportional-nums">{p.count}</span>
                            </div>
                        ))}
                        {(onb.by_path || []).length === 0 && (
                            <div className="text-xs text-muted-foreground">No signups in window.</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/* ─── Free-tier gate hits ────────────────────────────────────────────────── */
function FeatureLimitsTile({ gates, loading }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="tile-gates">
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Lock className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Free-tier gates · 7d</div>
                    <div className="text-xs text-muted-foreground">Which paid features free users try</div>
                </div>
            </div>
            {loading || !gates ? (
                <div className="text-xs text-muted-foreground">Loading…</div>
            ) : (
                <>
                    <div className="text-3xl font-mono font-extrabold mb-2" data-testid="gates-total">{gates.total}</div>
                    <div className="space-y-1">
                        {(gates.by_feature || []).map((f) => (
                            <div key={f.feature} className="flex items-center justify-between gap-2 text-xs" data-testid={`gate-feature-${f.feature}`}>
                                <span className="font-mono truncate">{f.feature}</span>
                                <span className="proportional-nums">
                                    <span className="font-bold">{f.hits}</span>
                                    <span className="text-muted-foreground ml-2">({f.unique_users}u)</span>
                                </span>
                            </div>
                        ))}
                        {(gates.by_feature || []).length === 0 && (
                            <div className="text-xs text-muted-foreground">No gate hits in window.</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function Stat({ label, value, tone, testId }) {
    const toneClass = tone === "bad" ? "text-destructive" : "";
    return (
        <div>
            <div className={`font-mono font-extrabold text-lg leading-none ${toneClass}`} data-testid={testId}>{value}</div>
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</div>
        </div>
    );
}

/* ─── Behavioral telemetry (fleet-wide aggregate, no PII) ────────────────── */
function BehavioralTelemetryTile({ behav, loading }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="tile-behavioral">
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Activity className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Behavioral · live</div>
                    <div className="text-xs text-muted-foreground">Sensor telemetry rollup</div>
                </div>
            </div>
            {loading || !behav ? (
                <div className="text-xs text-muted-foreground">Loading…</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-1.5 mb-3">
                        <Stat label="Tracked users" value={behav.tracked_users} testId="behavioral-tracked" />
                        <Stat label="Active · 1h" value={behav.active_last_hour} testId="behavioral-active" />
                    </div>
                    <div className="space-y-1 text-xs">
                        <div className="flex items-center justify-between" data-testid="behavioral-keystrokes">
                            <span className="text-muted-foreground">Avg keystrokes/h</span>
                            <span className="font-bold proportional-nums">{behav.avg_keystrokes_last_hour}</span>
                        </div>
                        <div className="flex items-center justify-between" data-testid="behavioral-flight">
                            <span className="text-muted-foreground">Avg flight (ms)</span>
                            <span className="font-bold proportional-nums">{behav.avg_flight_ms_last_hour}</span>
                        </div>
                        <div className="flex items-center justify-between" data-testid="behavioral-deletions">
                            <span className="text-muted-foreground">Avg deletion bursts</span>
                            <span className="font-bold proportional-nums">{behav.avg_deletion_bursts_last_hour}</span>
                        </div>
                        <div className="flex items-center justify-between" data-testid="behavioral-jitter">
                            <span className="text-muted-foreground">Avg cursor jitter</span>
                            <span className="font-bold proportional-nums">{behav.avg_jitter_variance_last_hour}</span>
                        </div>
                        <div className="flex items-center justify-between" data-testid="behavioral-focus">
                            <span className="text-muted-foreground">Avg focus blurs</span>
                            <span className="font-bold proportional-nums">{behav.avg_focus_blurs_last_hour}</span>
                        </div>
                    </div>
                    <div className="mt-2 text-[10px] text-muted-foreground italic">{behav.raw_batches_last_hour} raw batch(es) ingested in the last hour.</div>
                </>
            )}
        </div>
    );
}
